// Plan geometry. Exact predicates (orientation, crossing, point in polygon) use BigInt on
// integer micrometres, like the server's apps/api/factory/geometry.py, so the app and the
// server always agree on a polygon's shape. Sketch geometry (rough shapes in any unit) uses
// plain numbers.

import { nth } from "../lib/at";

export type Pt = readonly [number, number];

const big = (n: number) => BigInt(Math.round(n));

/** Sign of the cross product (b - a) × (c - a): 1 left turn, -1 right turn, 0 collinear. */
export function orient(a: Pt, b: Pt, c: Pt): -1 | 0 | 1 {
  const v = (big(b[0]) - big(a[0])) * (big(c[1]) - big(a[1])) - (big(b[1]) - big(a[1])) * (big(c[0]) - big(a[0]));
  return v > 0n ? 1 : v < 0n ? -1 : 0;
}

function onSegment(a: Pt, b: Pt, p: Pt): boolean {
  return (
    Math.min(a[0], b[0]) <= p[0] &&
    p[0] <= Math.max(a[0], b[0]) &&
    Math.min(a[1], b[1]) <= p[1] &&
    p[1] <= Math.max(a[1], b[1])
  );
}

/** True when the closed segments ab and cd share at least one point. */
export function segmentsTouch(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const o1 = orient(a, b, c);
  const o2 = orient(a, b, d);
  const o3 = orient(c, d, a);
  const o4 = orient(c, d, b);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(a, b, c)) return true;
  if (o2 === 0 && onSegment(a, b, d)) return true;
  if (o3 === 0 && onSegment(c, d, a)) return true;
  if (o4 === 0 && onSegment(c, d, b)) return true;
  return false;
}

/** Twice the signed area: negative for a clockwise polygon (x east, y north). */
export function twiceSignedArea(pts: readonly Pt[]): bigint {
  let s = 0n;
  for (let i = 0; i < pts.length; i++) {
    const a = nth(pts, i);
    const b = nth(pts, (i + 1) % pts.length);
    s += big(a[0]) * big(b[1]) - big(b[0]) * big(a[1]);
  }
  return s;
}

/** Pairs of wall indices that cross or overlap (neighbours may only share their corner). */
export function selfIntersections(pts: readonly Pt[]): [number, number][] {
  const n = pts.length;
  const out: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const a = nth(pts, i);
      const b = nth(pts, (i + 1) % n);
      const c = nth(pts, j);
      const d = nth(pts, (j + 1) % n);
      const neighbours = j === i + 1 || (i === 0 && j === n - 1);
      if (neighbours) {
        // Adjacent walls share one corner; they must not fold back over each other.
        const shared = j === i + 1 ? b : a;
        const other1 = j === i + 1 ? a : b;
        const other2 = j === i + 1 ? d : c;
        if (orient(shared, other1, other2) === 0) {
          const dot =
            (other1[0] - shared[0]) * (other2[0] - shared[0]) + (other1[1] - shared[1]) * (other2[1] - shared[1]);
          if (dot > 0) out.push([i, j]);
        }
        continue;
      }
      if (segmentsTouch(a, b, c, d)) out.push([i, j]);
    }
  }
  return out;
}

/** Point strictly inside or on the boundary of the polygon. */
export function pointInPolygon(p: Pt, pts: readonly Pt[]): boolean {
  const n = pts.length;
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = nth(pts, i);
    const b = nth(pts, j);
    if (orient(a, b, p) === 0 && onSegment(a, b, p)) return true;
    if (a[1] > p[1] !== b[1] > p[1]) {
      // x of the edge at p's y, compared exactly: (p.x - a.x) * (b.y - a.y) vs (b.x - a.x) * (p.y - a.y)
      const lhs = (big(p[0]) - big(a[0])) * (big(b[1]) - big(a[1]));
      const rhs = (big(b[0]) - big(a[0])) * (big(p[1]) - big(a[1]));
      const crosses = b[1] > a[1] ? lhs < rhs : lhs > rhs;
      if (crosses) inside = !inside;
    }
  }
  return inside;
}

export function dist(a: Pt, b: Pt): number {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

/** Interior angle at each corner of a clockwise polygon, in degrees. */
export function interiorAngles(pts: readonly Pt[]): number[] {
  const n = pts.length;
  return pts.map((p, i) => {
    const prev = nth(pts, (i - 1 + n) % n);
    const next = nth(pts, (i + 1) % n);
    // Clockwise outline: the inside is the counter-clockwise sweep from (prev - p) to (next - p).
    const u = [prev[0] - p[0], prev[1] - p[1]] as const;
    const v = [next[0] - p[0], next[1] - p[1]] as const;
    const deg = (Math.atan2(u[0] * v[1] - u[1] * v[0], u[0] * v[0] + u[1] * v[1]) * 180) / Math.PI;
    return (deg + 360) % 360;
  });
}

/** Area in square metres of a polygon given in micrometres. */
export function areaM2(pts: readonly Pt[]): number {
  let s = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = nth(pts, i);
    const b = nth(pts, (i + 1) % pts.length);
    s += a[0] * b[1] - b[0] * a[1];
  }
  return Math.abs(s) / 2 / 1e12;
}

/** Float version for sketches: is the diagonal a-b of the polygon inside it? */
export function diagonalInside(pts: readonly Pt[], a: number, b: number): boolean {
  const n = pts.length;
  if (a === b || (a + 1) % n === b || (b + 1) % n === a) return false;
  const pa = nth(pts, a);
  const pb = nth(pts, b);
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    if (i === a || i === b || j === a || j === b) continue;
    if (segmentsTouchF(pa, pb, nth(pts, i), nth(pts, j))) return false;
  }
  // Also never pass through another corner.
  for (let k = 0; k < n; k++) {
    if (k === a || k === b) continue;
    if (orientF(pa, pb, nth(pts, k)) === 0 && onSegment(pa, pb, nth(pts, k))) return false;
  }
  const mid: Pt = [(pa[0] + pb[0]) / 2, (pa[1] + pb[1]) / 2];
  return pointInPolygonF(mid, pts);
}

function orientF(a: Pt, b: Pt, c: Pt): -1 | 0 | 1 {
  const v = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  const eps = 1e-9 * (Math.abs(b[0] - a[0]) + Math.abs(b[1] - a[1]) + 1) ** 2;
  return v > eps ? 1 : v < -eps ? -1 : 0;
}

function segmentsTouchF(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const o1 = orientF(a, b, c);
  const o2 = orientF(a, b, d);
  const o3 = orientF(c, d, a);
  const o4 = orientF(c, d, b);
  if (o1 !== o2 && o3 !== o4) return true;
  return (
    (o1 === 0 && onSegment(a, b, c)) ||
    (o2 === 0 && onSegment(a, b, d)) ||
    (o3 === 0 && onSegment(c, d, a)) ||
    (o4 === 0 && onSegment(c, d, b))
  );
}

function pointInPolygonF(p: Pt, pts: readonly Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = nth(pts, i);
    const b = nth(pts, j);
    if (a[1] > p[1] !== b[1] > p[1] && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0]) {
      inside = !inside;
    }
  }
  return inside;
}

/** Signed area (float) of a sketch: negative means clockwise. */
export function sketchArea(pts: readonly Pt[]): number {
  let s = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = nth(pts, i);
    const b = nth(pts, (i + 1) % pts.length);
    s += a[0] * b[1] - b[0] * a[1];
  }
  return s / 2;
}

/** Sketch problems: fewer than 3 corners, crossing walls. */
export function sketchSimple(pts: readonly Pt[]): boolean {
  const n = pts.length;
  if (n < 3) return false;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const neighbours = j === i + 1 || (i === 0 && j === n - 1);
      if (neighbours) continue;
      if (segmentsTouchF(nth(pts, i), nth(pts, (i + 1) % n), nth(pts, j), nth(pts, (j + 1) % n))) return false;
    }
  }
  return Math.abs(sketchArea(pts)) > 0;
}
